import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Type, Square, Circle, Image as ImageIcon, Sparkles,
    PlusCircle, Triangle, Star, Hexagon, ArrowRight,
    Search, Wand2, Type as FontIcon, Layout,
    Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify,
    List, ListOrdered, Palette, Trash2, ArrowBigUp, ArrowBigDown,
    ChevronRight, CaseSensitive, MessageSquare, Highlighter, Eraser, ChevronDown, ALargeSmall, ChevronUp, PieChart,
    RefreshCw, Check, ArrowRightFromLine, MoveDown, SpellCheck,
    RefreshCcwDot,
    CheckCheck,
    ArrowDownWideNarrow,
    WrapText,
    BarChart2,
    StepForward,
    ArrowUp
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ShapeType } from '../lib/types';

interface SlashMenuItem {
    id: string;
    label: string;
    icon: React.ElementType;
    category: 'Basic' | 'Shapes' | 'AI' | 'Canvas' | 'Formatting' | 'Alignment' | 'List' | 'Actions';
    description?: string;
    action: () => void;
    currentValue?: any;
}

interface SlashMenuProps {
    onClose: () => void;
    onAction: (actionId: string, payload?: any) => void;
    position: { x: number; y: number };
    canUseAi: boolean;
    mode?: 'default' | 'textbox' | 'text-selection';
    context?: {
        fontFamily?: string;
        fontSize?: number;
        isBold?: boolean;
        isItalic?: boolean;
        isUnderline?: boolean;
        isBulleted?: boolean;
        isNumbered?: boolean;
        textAlign?: 'left' | 'center' | 'right' | 'justify';
        color?: string;
        highlightColor?: string;
    };
    aiResult?: string | null;
    isAiLoading?: boolean;
    lastAiAction?: 'shorten' | 'reframe' | 'lengthen' | 'rewrite' | 'grammar' | 'continue' | 'custom';
    onAiResponseAction?: (action: 'replace' | 'addBelow' | 'discard' | 'regenerate') => void;
}

const ACTION_CONFIG = {
    rewrite: { label: 'Improve writing', icon: RefreshCcwDot, color: 'text-blue-500' },
    grammar: { label: 'Fix grammar', icon: CheckCheck, color: 'text-blue-500' },
    shorten: { label: 'Make shorter', icon: ArrowDownWideNarrow, color: 'text-blue-500' },
    lengthen: { label: 'Make longer', icon: WrapText, color: 'text-blue-500' },
    continue: { label: 'Continue writing', icon: StepForward, color: 'text-blue-500' },
    reframe: { label: 'Reframe', icon: RefreshCw, color: 'text-blue-500' },
    custom: { label: 'AI Edit', icon: Sparkles, color: 'text-blue-500' },
};

const FONTS = [
    'Inter', 'Arial', 'Lato', 'Open Sans', 'Poppins', 'Roboto', 'Georgia', 'Times New Roman'
];

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64, 72, 96];

export const SlashMenu: React.FC<SlashMenuProps> = ({ onClose, onAction, position, canUseAi, mode = 'default', context, aiResult, isAiLoading, onAiResponseAction, lastAiAction }) => {
    const [search, setSearch] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showFontDropdown, setShowFontDropdown] = useState(false);
    const [showSizeDropdown, setShowSizeDropdown] = useState(false);

    const [showBulletMenu, setShowBulletMenu] = useState(false);
    const [showNumberingMenu, setShowNumberingMenu] = useState(false);
    const [showAiMenu, setShowAiMenu] = useState(false);
    const [aiQuery, setAiQuery] = useState('');

    // Default vertical menu items
    const defaultItems = useMemo(() => [
        { id: 'generate-image', label: 'AI Generate Image', icon: Sparkles, category: 'AI', description: 'Create an image with AI prompt', action: () => onAction('generate-image') },
        { id: 'generate-infographic', label: 'AI Generate Infographic', icon: PieChart, category: 'AI', description: 'Create an infographic with AI prompt', action: () => onAction('generate-infographic') },
        { id: 'text', label: 'Text Box', icon: Type, category: 'Basic', description: 'Insert a new text element', action: () => onAction('add-text') },
        { id: 'image', label: 'Image', icon: ImageIcon, category: 'Basic', description: 'Upload and insert an image', action: () => onAction('add-image') },
        { id: 'new-slide', label: 'New Slide', icon: PlusCircle, category: 'Canvas', description: 'Add a fresh slide to deck', action: () => onAction('new-slide') },
        { id: 'rect', label: 'Rectangle', icon: Square, category: 'Shapes', action: () => onAction('add-shape', 'rect') },
        { id: 'circle', label: 'Circle', icon: Circle, category: 'Shapes', action: () => onAction('add-shape', 'circle') },
        { id: 'triangle', label: 'Triangle', icon: Triangle, category: 'Shapes', action: () => onAction('add-shape', 'triangle') },
        { id: 'star5', label: 'Star', icon: Star, category: 'Shapes', action: () => onAction('add-shape', 'star5') },
        { id: 'arrowRight', label: 'Arrow', icon: ArrowRight, category: 'Shapes', action: () => onAction('add-shape', 'arrowRight') },
    ], [onAction]);

    const filteredItems = useMemo(() => {
        return defaultItems.filter(item =>
            item.label.toLowerCase().includes(search.toLowerCase()) ||
            item.category.toLowerCase().includes(search.toLowerCase())
        );
    }, [defaultItems, search]);

    useEffect(() => {
        setSelectedIndex(0);
    }, [search]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (mode === 'default') {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedIndex(prev => (prev + 1) % filteredItems.length);
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedIndex(prev => (prev - 1 + filteredItems.length) % filteredItems.length);
                } else if (e.key === 'Enter') {
                    e.preventDefault();
                    if (filteredItems[selectedIndex]) {
                        filteredItems[selectedIndex].action();
                    }
                }
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [filteredItems, selectedIndex, onClose, mode]);

    // Ensure selected item is visible in scroll container (only for default mode)
    useEffect(() => {
        if (mode === 'default' && scrollContainerRef.current) {
            const selectedElement = scrollContainerRef.current.children[selectedIndex] as HTMLElement;
            if (selectedElement) {
                selectedElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [selectedIndex, mode]);

    if (mode === 'default' && filteredItems.length === 0 && search === '') return null;

    // Redesign for horizontal toolbar (PowerPoint style)
    if (mode === 'textbox' || mode === 'text-selection') {
        const activeFont = context?.fontFamily || 'Arial';
        const activeSize = context?.fontSize || 24;

        // Dynamic positioning ref
        const menuRef = useRef<HTMLDivElement>(null);
        const [adjustedPosition, setAdjustedPosition] = useState<{ x: number, y: number } | null>(null);

        React.useLayoutEffect(() => {
            if (menuRef.current) {
                const rect = menuRef.current.getBoundingClientRect();
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;

                let x = position.x;
                let y = position.y;

                // Check horizontal overflow
                if (x + rect.width > viewportWidth - 100) {
                    x = viewportWidth - rect.width - 100;
                }
                x = Math.max(10, x); // Keep at least 10px from left

                // Check vertical overflow
                if (y + rect.height > viewportHeight - 20) {
                    y = viewportHeight - rect.height - 20; // Flip up or push up
                }

                setAdjustedPosition({ x, y });
            }
        }, [position.x, position.y]); // Re-run when target position changes

        // Use adjusted position if available, otherwise hide initially (opacity 0) to prevent flash
        const stylePosition = adjustedPosition || { x: position.x, y: position.y };
        const opacity = adjustedPosition ? 1 : 0;

        // Determine if dropdown should open upwards
        const openUpwards = stylePosition.y > window.innerHeight / 2;

        return (
            <div
                ref={menuRef}
                className="fixed z-[9999] bg-white rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-slate-200 p-2.5 flex flex-col gap-2.5 animate-in fade-in zoom-in-95 duration-200"
                style={{
                    left: `${stylePosition.x}px`,
                    top: `${stylePosition.y}px`,
                    width: 'auto',
                    minWidth: '440px',
                    maxWidth: '90vw',
                    opacity: opacity
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Row 1: Font Selectors and Alignment */}
                <div className="flex items-center gap-1.5 h-9">
                    <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg h-full flex-1 shadow-sm">
                        <div className="relative flex-1">
                            <button
                                onClick={() => { setShowFontDropdown(!showFontDropdown); setShowSizeDropdown(false); }}
                                className="w-full flex items-center justify-between px-3 h-full text-xs font-bold text-slate-800 hover:bg-white transition-colors border-r border-slate-100 rounded-l-lg"
                            >
                                <span className="truncate">{activeFont}</span>
                                <ChevronDown size={14} className="text-slate-400" />
                            </button>
                            {showFontDropdown && (
                                <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-2xl z-50 py-1 min-w-[180px] max-h-[250px] overflow-y-auto animate-in fade-in slide-in-from-top-2">
                                    {FONTS.map(font => (
                                        <button
                                            key={font}
                                            onClick={() => { onAction('format', { fontFamily: font }); setShowFontDropdown(false); }}
                                            className={cn(
                                                "w-full text-left px-4 py-2 text-xs hover:bg-blue-50 transition-colors",
                                                activeFont === font ? "text-blue-600 font-bold bg-blue-100/50" : "text-slate-700"
                                            )}
                                            style={{ fontFamily: font }}
                                        >
                                            {font}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="relative w-16">
                            <button
                                onClick={() => { setShowSizeDropdown(!showSizeDropdown); setShowFontDropdown(false); }}
                                className="w-full flex items-center justify-between px-2 h-full text-xs font-black text-slate-900 hover:bg-white transition-colors rounded-r-lg"
                            >
                                <span>{activeSize}</span>
                                <ChevronDown size={14} className="text-slate-400" />
                            </button>
                            {showSizeDropdown && (
                                <div className="absolute top-full right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-2xl z-50 py-1 min-w-[80px] max-h-[250px] overflow-y-auto animate-in fade-in slide-in-from-top-2">
                                    {FONT_SIZES.map(size => (
                                        <button
                                            key={size}
                                            onClick={() => { onAction('format', { fontSize: size }); setShowSizeDropdown(false); }}
                                            className={cn(
                                                "w-full text-center px-4 py-2 text-xs hover:bg-blue-50 transition-colors",
                                                activeSize === size ? "text-blue-600 font-bold bg-blue-100/50" : "text-slate-700"
                                            )}
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-1 px-1 bg-slate-50 border border-slate-200 rounded-lg h-full shadow-sm">
                        <button onClick={() => onAction('format', { fontSize: activeSize + 2 })} className="p-1 hover:bg-white hover:text-blue-600 rounded-md text-slate-500 transition-all active:scale-95 flex items-center justify-center" title="Increase font size">
                            <span className="text-[13px] font-black leading-none translate-y-[1px]">A</span>
                            <ChevronUp size={10} strokeWidth={4} className="ml-0.5" />
                        </button>
                        <button onClick={() => onAction('format', { fontSize: Math.max(1, activeSize - 2) })} className="p-1 hover:bg-white hover:text-blue-600 rounded-md text-slate-500 transition-all active:scale-95 flex items-center justify-center" title="Decrease font size">
                            <span className="text-[11px] font-black leading-none translate-y-[1px]">A</span>
                            <ChevronDown size={10} strokeWidth={4} className="ml-0.5" />
                        </button>
                    </div>


                </div>

                {/* Row 2: Tool Icons */}
                <div className="flex items-center gap-0.5 border-t border-slate-100 pt-2.5 flex-nowrap">
                    <button
                        onClick={() => onAction('format', { isBold: !context?.isBold })}
                        className={cn("p-1 rounded-[3px] transition-all", context?.isBold ? "text-blue-600 bg-blue-50 shadow-sm" : "text-slate-200 hover:bg-slate-50 hover:text-slate-500")}
                        title="Bold"
                    >
                        <Bold size={14} strokeWidth={2.5} />
                    </button>
                    <button
                        onClick={() => onAction('format', { isItalic: !context?.isItalic })}
                        className={cn("p-1 rounded-[3px] transition-all", context?.isItalic ? "text-blue-600 bg-blue-50 shadow-sm" : "text-slate-200 hover:bg-slate-50 hover:text-slate-500")}
                        title="Italic"
                    >
                        <Italic size={14} strokeWidth={2.5} />
                    </button>
                    <button
                        onClick={() => onAction('format', { isUnderline: !context?.isUnderline })}
                        className={cn("p-1 rounded-[3px] transition-all", context?.isUnderline ? "text-blue-600 bg-blue-50 shadow-sm" : "text-slate-200 hover:bg-slate-50 hover:text-slate-500")}
                        title="Underline"
                    >
                        <Underline size={14} strokeWidth={2.5} />
                    </button>

                    <div className="w-[1px] h-4 bg-slate-100 mx-1.5" />

                    {/* Ask AI - Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setShowAiMenu(!showAiMenu)}
                            className={cn(
                                "flex items-center gap-1 px-1.5 py-0.5 rounded-[3px] transition-all text-xs font-bold whitespace-nowrap",
                                showAiMenu
                                    ? "bg-blue-50 text-blue-600 border-blue-100"
                                    : "text-slate-500 hover:bg-blue-50 hover:text-blue-600"
                            )}
                        >
                            {/* <Magic size={12} className={showAiMenu ? "fill-purple-300" : ""} /> */}
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 469 469"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                shapeRendering="geometricPrecision"
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.5"
                            >

                                <path
                                    d="M237.092 62.3004L266.754 71.4198C267.156 71.5285 267.51 71.765 267.765 72.0934C268.02 72.4218 268.161 72.8243 268.166 73.2399C268.172 73.6555 268.042 74.0616 267.796 74.3967C267.55 74.7318 267.201 74.9777 266.803 75.097L237.141 84.3145C236.84 84.4058 236.566 84.5699 236.344 84.7922C236.121 85.0146 235.957 85.2883 235.866 85.5893L226.747 115.252C226.638 115.653 226.401 116.008 226.073 116.263C225.745 116.517 225.342 116.658 224.926 116.664C224.511 116.669 224.105 116.539 223.77 116.293C223.435 116.047 223.189 115.699 223.069 115.301L213.852 85.6383C213.761 85.3374 213.597 85.0636 213.374 84.8412C213.152 84.6189 212.878 84.4548 212.577 84.3635L182.914 75.2441C182.513 75.1354 182.158 74.8989 181.904 74.5705C181.649 74.2421 181.508 73.8396 181.503 73.424C181.497 73.0084 181.627 72.6023 181.873 72.2672C182.119 71.9321 182.467 71.6863 182.865 71.5669L212.528 62.3494C212.829 62.2582 213.103 62.0941 213.325 61.8717C213.547 61.6494 213.712 61.3756 213.803 61.0747L222.922 31.4121C223.031 31.0109 223.267 30.656 223.596 30.4013C223.924 30.1465 224.327 30.0057 224.742 30.0002C225.158 29.9946 225.564 30.1247 225.899 30.3706C226.234 30.6165 226.48 30.9649 226.599 31.363L235.817 61.0257C235.908 61.3266 236.072 61.6003 236.295 61.8227C236.517 62.0451 236.791 62.2091 237.092 62.3004Z"
                                    fill="currentColor"
                                />
                                <path
                                    d="M155.948 155.848L202.771 168.939C203.449 169.131 204.045 169.539 204.47 170.101C204.895 170.663 205.125 171.348 205.125 172.052C205.125 172.757 204.895 173.442 204.47 174.004C204.045 174.566 203.449 174.974 202.771 175.166L155.899 188.06C155.361 188.209 154.87 188.496 154.475 188.891C154.079 189.286 153.793 189.777 153.644 190.316L140.553 237.138C140.361 237.816 139.953 238.413 139.391 238.838C138.829 239.262 138.144 239.492 137.44 239.492C136.735 239.492 136.05 239.262 135.488 238.838C134.927 238.413 134.519 237.816 134.327 237.138L121.432 190.267C121.283 189.728 120.997 189.237 120.601 188.842C120.206 188.446 119.715 188.16 119.177 188.011L72.3537 174.92C71.676 174.728 71.0795 174.32 70.6547 173.759C70.2299 173.197 70 172.512 70 171.807C70 171.103 70.2299 170.418 70.6547 169.856C71.0795 169.294 71.676 168.886 72.3537 168.694L119.226 155.799C119.764 155.65 120.255 155.364 120.65 154.969C121.046 154.573 121.332 154.082 121.481 153.544L134.572 106.721C134.764 106.043 135.172 105.447 135.734 105.022C136.295 104.597 136.981 104.367 137.685 104.367C138.389 104.367 139.075 104.597 139.637 105.022C140.198 105.447 140.606 106.043 140.798 106.721L153.693 153.593C153.842 154.131 154.128 154.622 154.524 155.018C154.919 155.413 155.41 155.699 155.948 155.848Z"
                                    fill="currentColor"
                                />
                                <path
                                    d="M386.827 289.992C404.33 292.149 403.84 305.828 386.876 307.299C346.623 310.829 298.869 316.271 282.199 360.005C274.844 379.192 269.942 403.2 267.49 432.029C267.427 432.846 267.211 433.626 266.856 434.319C266.501 435.012 266.015 435.602 265.431 436.05C254.988 444.041 251.212 434.186 250.183 425.606C239.2 332.353 214.588 316.909 124.668 306.122C123.892 306.031 123.151 305.767 122.504 305.35C121.857 304.933 121.322 304.375 120.942 303.72C116.399 295.679 119.324 291.038 129.718 289.796C224.688 278.47 236.062 262.83 250.183 169.331C252.177 156.355 257.259 154.083 265.431 162.516C266.51 163.593 267.202 165.099 267.392 166.782C279.257 258.564 293.328 278.617 386.827 289.992Z"
                                    fill="currentColor"
                                />
                            </svg>
                            <span>Ask AI</span>
                            <ChevronDown size={10} className="opacity-50" />
                        </button>

                        {showAiMenu && (
                            <div className={cn(
                                "absolute left-0 w-80 bg-white border border-slate-200 rounded-xl shadow-2xl z-[1000] animate-in fade-in zoom-in-95 duration-200 overflow-hidden text-left flex flex-col max-h-[300px]",
                                openUpwards ? "bottom-full mb-2 origin-bottom-left" : "top-full mt-2 origin-top-left"
                            )}>
                                {(aiResult || isAiLoading) && lastAiAction && ACTION_CONFIG[lastAiAction] ? (
                                    <div className="px-3 py-2.5 bg-blue-50/50 border-b border-blue-100 flex items-center gap-2">
                                        <div className={cn("shrink-0", ACTION_CONFIG[lastAiAction].color)}>
                                            {React.createElement(ACTION_CONFIG[lastAiAction].icon, { size: 14 })}
                                        </div>
                                        <span className={cn("text-xs font-bold uppercase tracking-wider", ACTION_CONFIG[lastAiAction].color.replace('500', '700'))}>
                                            {ACTION_CONFIG[lastAiAction].label}
                                        </span>
                                    </div>
                                ) : (
                                    <div className="px-3 py-2.5 bg-white border-b border-slate-100 flex items-center gap-2">
                                        <div className="text-blue-500 shrink-0">
                                            <svg
                                                width="15"
                                                height="15"
                                                viewBox="0 0 469 469"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                shapeRendering="geometricPrecision"
                                                stroke="currentColor"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="1.5"
                                            >

                                                <path
                                                    d="M237.092 62.3004L266.754 71.4198C267.156 71.5285 267.51 71.765 267.765 72.0934C268.02 72.4218 268.161 72.8243 268.166 73.2399C268.172 73.6555 268.042 74.0616 267.796 74.3967C267.55 74.7318 267.201 74.9777 266.803 75.097L237.141 84.3145C236.84 84.4058 236.566 84.5699 236.344 84.7922C236.121 85.0146 235.957 85.2883 235.866 85.5893L226.747 115.252C226.638 115.653 226.401 116.008 226.073 116.263C225.745 116.517 225.342 116.658 224.926 116.664C224.511 116.669 224.105 116.539 223.77 116.293C223.435 116.047 223.189 115.699 223.069 115.301L213.852 85.6383C213.761 85.3374 213.597 85.0636 213.374 84.8412C213.152 84.6189 212.878 84.4548 212.577 84.3635L182.914 75.2441C182.513 75.1354 182.158 74.8989 181.904 74.5705C181.649 74.2421 181.508 73.8396 181.503 73.424C181.497 73.0084 181.627 72.6023 181.873 72.2672C182.119 71.9321 182.467 71.6863 182.865 71.5669L212.528 62.3494C212.829 62.2582 213.103 62.0941 213.325 61.8717C213.547 61.6494 213.712 61.3756 213.803 61.0747L222.922 31.4121C223.031 31.0109 223.267 30.656 223.596 30.4013C223.924 30.1465 224.327 30.0057 224.742 30.0002C225.158 29.9946 225.564 30.1247 225.899 30.3706C226.234 30.6165 226.48 30.9649 226.599 31.363L235.817 61.0257C235.908 61.3266 236.072 61.6003 236.295 61.8227C236.517 62.0451 236.791 62.2091 237.092 62.3004Z"
                                                    fill="currentColor"
                                                />
                                                <path
                                                    d="M155.948 155.848L202.771 168.939C203.449 169.131 204.045 169.539 204.47 170.101C204.895 170.663 205.125 171.348 205.125 172.052C205.125 172.757 204.895 173.442 204.47 174.004C204.045 174.566 203.449 174.974 202.771 175.166L155.899 188.06C155.361 188.209 154.87 188.496 154.475 188.891C154.079 189.286 153.793 189.777 153.644 190.316L140.553 237.138C140.361 237.816 139.953 238.413 139.391 238.838C138.829 239.262 138.144 239.492 137.44 239.492C136.735 239.492 136.05 239.262 135.488 238.838C134.927 238.413 134.519 237.816 134.327 237.138L121.432 190.267C121.283 189.728 120.997 189.237 120.601 188.842C120.206 188.446 119.715 188.16 119.177 188.011L72.3537 174.92C71.676 174.728 71.0795 174.32 70.6547 173.759C70.2299 173.197 70 172.512 70 171.807C70 171.103 70.2299 170.418 70.6547 169.856C71.0795 169.294 71.676 168.886 72.3537 168.694L119.226 155.799C119.764 155.65 120.255 155.364 120.65 154.969C121.046 154.573 121.332 154.082 121.481 153.544L134.572 106.721C134.764 106.043 135.172 105.447 135.734 105.022C136.295 104.597 136.981 104.367 137.685 104.367C138.389 104.367 139.075 104.597 139.637 105.022C140.198 105.447 140.606 106.043 140.798 106.721L153.693 153.593C153.842 154.131 154.128 154.622 154.524 155.018C154.919 155.413 155.41 155.699 155.948 155.848Z"
                                                    fill="currentColor"
                                                />
                                                <path
                                                    d="M386.827 289.992C404.33 292.149 403.84 305.828 386.876 307.299C346.623 310.829 298.869 316.271 282.199 360.005C274.844 379.192 269.942 403.2 267.49 432.029C267.427 432.846 267.211 433.626 266.856 434.319C266.501 435.012 266.015 435.602 265.431 436.05C254.988 444.041 251.212 434.186 250.183 425.606C239.2 332.353 214.588 316.909 124.668 306.122C123.892 306.031 123.151 305.767 122.504 305.35C121.857 304.933 121.322 304.375 120.942 303.72C116.399 295.679 119.324 291.038 129.718 289.796C224.688 278.47 236.062 262.83 250.183 169.331C252.177 156.355 257.259 154.083 265.431 162.516C266.51 163.593 267.202 165.099 267.392 166.782C279.257 258.564 293.328 278.617 386.827 289.992Z"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                        </div>
                                        <input
                                            type="text"
                                            value={aiQuery}
                                            onChange={(e) => setAiQuery(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && aiQuery.trim()) {
                                                    onAction('ai-custom-edit', aiQuery);
                                                }
                                            }}
                                            placeholder="Ask AI to edit selected text..."
                                            className="flex-1 text-xs text-slate-700 placeholder:text-slate-400 outline-none bg-transparent"
                                            autoFocus={!aiResult}
                                        />
                                        <button
                                            onClick={() => {
                                                if (aiQuery.trim()) {
                                                    onAction('ai-custom-edit', aiQuery);
                                                }
                                            }}
                                            className="p-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors shrink-0 flex items-center justify-center shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                            disabled={!aiQuery.trim()}
                                        >
                                            <ArrowUp className="h-4 w-4" />
                                        </button>
                                    </div>
                                )}

                                {isAiLoading ? (
                                    <div className="p-8 flex flex-col items-center justify-center gap-3 text-slate-400">
                                        <RefreshCw size={24} className="animate-spin text-purple-500" />
                                        <span className="text-xs font-medium">AI is thinking...</span>
                                    </div>
                                ) : aiResult ? (
                                    <div className="flex flex-col overflow-y-auto max-h-[200px]">
                                        <div className="p-4 bg-slate-50/50 text-sm text-slate-700 leading-relaxed border-b border-slate-100">
                                            {aiResult}
                                        </div>
                                        <div className="p-1.5 flex flex-col gap-0.5">
                                            <button
                                                onClick={() => { onAiResponseAction?.('replace'); setShowAiMenu(false); }}
                                                className="flex items-center gap-2 px-1 py-1 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-left"
                                            >
                                                <Check size={14} className="text-slate-400" />
                                                <span>Replace selection</span>
                                            </button>
                                            <button
                                                onClick={() => { onAiResponseAction?.('addBelow'); setShowAiMenu(false); }}
                                                className="flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-left"
                                            >
                                                <ArrowRightFromLine size={14} className="text-slate-400 rotate-90" />
                                                <span>Insert below</span>
                                            </button>
                                            <button
                                                onClick={() => { onAiResponseAction?.('regenerate'); setShowAiMenu(false); }}
                                                className="flex items-center gap-2 px-2 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-left"
                                            >
                                                <RefreshCw size={14} />
                                                <span>Try again</span>
                                            </button>
                                            <div className="h-[1px] bg-slate-100 my-1" />
                                            <button
                                                onClick={() => { onAiResponseAction?.('discard'); setShowAiMenu(false); }}
                                                className="flex items-center gap-2 px-2 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors text-left"
                                            >
                                                <Trash2 size={14} />
                                                <span>Discard</span>
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="p-1.5 flex flex-col gap-0.5 overflow-y-auto max-h-[200px]">
                                        <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Edit Selection</div>
                                        <button
                                            onClick={() => { onAction('ai-text-edit', 'rewrite'); }}
                                            className="flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-left"
                                        >
                                            <RefreshCcwDot size={14} className="text-blue-500" />
                                            <span>Improve writing</span>
                                        </button>
                                        <button
                                            onClick={() => { onAction('ai-text-edit', 'grammar'); }}
                                            className="flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-left"
                                        >
                                            <CheckCheck size={14} className="text-blue-500" />
                                            <span>Fix grammar</span>
                                        </button>
                                        <button
                                            onClick={() => { onAction('ai-text-edit', 'shorten'); }}
                                            className="flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-left"
                                        >
                                            <ArrowDownWideNarrow size={14} className="text-blue-500" />
                                            <span>Make shorter</span>
                                        </button>
                                        <button
                                            onClick={() => { onAction('ai-text-edit', 'lengthen'); }}
                                            className="flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-left"
                                        >
                                            <WrapText size={14} className="text-blue-500" />
                                            <span>Make longer</span>
                                        </button>

                                        <div className="h-[1px] bg-slate-100 my-1" />

                                        <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Generate</div>
                                        <button
                                            onClick={() => { onAction('generate-image'); setShowAiMenu(false); }}
                                            className="flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-left"
                                        >
                                            <ImageIcon size={14} className="text-blue-500" />
                                            <span>Generate Image</span>
                                        </button>
                                        <button
                                            onClick={() => { onAction('generate-infographic'); setShowAiMenu(false); }}
                                            className="flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-left"
                                        >
                                            <BarChart2 size={14} className="text-blue-500" />
                                            <span>Generate Infographic</span>
                                        </button>

                                        <div className="h-[1px] bg-slate-100 my-1" />

                                        <div className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest">More</div>
                                        <button
                                            onClick={() => { onAction('ai-text-edit', 'continue'); }}
                                            className="flex items-center gap-2 px-2 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg transition-colors text-left"
                                        >
                                            <StepForward size={14} className="text-blue-500" />
                                            <span>Continue writing</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="w-[1px] h-4 bg-slate-100 mx-1.5" />

                    {/* Bullet List Split Button */}
                    <div className="relative flex items-center rounded-md transition-all">
                        <button
                            onClick={() => onAction('format', { isBulleted: !context?.isBulleted, isNumbered: false, listType: !context?.isBulleted ? 'bullet-disc' : 'none' })}
                            className={cn("p-1 rounded-l-[3px] transition-all", context?.isBulleted ? "text-blue-600 bg-blue-50" : "text-slate-500 hover:bg-slate-50")} title="Bullets"
                        >
                            <List size={14} strokeWidth={2.5} />
                        </button>
                        <button
                            onClick={() => setShowBulletMenu(!showBulletMenu)}
                            className={cn("p-0.5 rounded-r-md border-l border-transparent hover:border-slate-200 transition-all", showBulletMenu ? "bg-slate-100" : "hover:bg-slate-50")}
                        >
                            <ChevronDown size={10} className="text-slate-400" />
                        </button>
                        {showBulletMenu && (
                            <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl p-2 z-[100] animate-in fade-in zoom-in-95 duration-150 w-64 grid grid-cols-4 gap-2">
                                <button onClick={() => { onAction('format', { isBulleted: false, listType: 'none' }); setShowBulletMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1">
                                    <span className="text-[10px] text-slate-500">None</span>
                                </button>
                                <button onClick={() => { onAction('format', { isBulleted: true, isNumbered: false, listType: 'bullet-disc' }); setShowBulletMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1"><div className="flex flex-col gap-1 items-start w-full px-1"><div className="flex items-center gap-1"><div className="w-1 h-1 rounded-full bg-black"></div><div className="w-6 h-[1px] bg-slate-300"></div></div><div className="flex items-center gap-1"><div className="w-1 h-1 rounded-full bg-black"></div><div className="w-6 h-[1px] bg-slate-300"></div></div></div></button>
                                <button onClick={() => { onAction('format', { isBulleted: true, isNumbered: false, listType: 'bullet-circle' }); setShowBulletMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1"><div className="flex flex-col gap-1 items-start w-full px-1"><div className="flex items-center gap-1"><div className="w-1 h-1 rounded-full border border-black bg-transparent"></div><div className="w-6 h-[1px] bg-slate-300"></div></div><div className="flex items-center gap-1"><div className="w-1 h-1 rounded-full border border-black bg-transparent"></div><div className="w-6 h-[1px] bg-slate-300"></div></div></div></button>
                                <button onClick={() => { onAction('format', { isBulleted: true, isNumbered: false, listType: 'bullet-square' }); setShowBulletMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1"><div className="flex flex-col gap-1 items-start w-full px-1"><div className="flex items-center gap-1"><div className="w-1 h-1 bg-black"></div><div className="w-6 h-[1px] bg-slate-300"></div></div><div className="flex items-center gap-1"><div className="w-1 h-1 bg-black"></div><div className="w-6 h-[1px] bg-slate-300"></div></div></div></button>
                                <button onClick={() => { onAction('format', { isBulleted: true, isNumbered: false, listType: 'bullet-check' }); setShowBulletMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1"><div className="flex flex-col gap-1 items-start w-full px-1"><div className="flex items-center gap-1"><span className="text-[6px]">✓</span><div className="w-6 h-[1px] bg-slate-300"></div></div><div className="flex items-center gap-1"><span className="text-[6px]">✓</span><div className="w-6 h-[1px] bg-slate-300"></div></div></div></button>
                            </div>
                        )}
                    </div>

                    {/* Numbered List Split Button */}
                    <div className="relative text-black flex items-center rounded-md transition-all">
                        <button
                            onClick={() => onAction('format', { isNumbered: !context?.isNumbered, isBulleted: false, listType: !context?.isNumbered ? 'number-decimal' : 'none' })}
                            className={cn("p-1 rounded-l-[3px] transition-all", context?.isNumbered ? "text-blue-600 bg-blue-50" : "text-slate-500 hover:bg-slate-50")} title="Numbering"
                        >
                            <ListOrdered size={14} strokeWidth={2.5} />
                        </button>
                        <button
                            onClick={() => setShowNumberingMenu(!showNumberingMenu)}
                            className={cn("p-0.5 rounded-r-md border-l border-transparent hover:border-slate-200 transition-all", showNumberingMenu ? "bg-slate-100" : "hover:bg-slate-50")}
                        >
                            <ChevronDown size={10} className="text-slate-400" />
                        </button>
                        {showNumberingMenu && (
                            <div className="absolute text-black top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl p-2 z-[100] animate-in fade-in zoom-in-95 duration-150 w-64 grid grid-cols-4 gap-2">
                                <button onClick={() => { onAction('format', { isNumbered: false, listType: 'none' }); setShowNumberingMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1">
                                    <span className="text-[10px] text-slate-500">None</span>
                                </button>
                                <button onClick={() => { onAction('format', { isNumbered: true, isBulleted: false, listType: 'number-decimal' }); setShowNumberingMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1"><div className="flex flex-col gap-1 items-start w-full px-1"><div className="flex items-center gap-1"><span className="text-[6px]">1.</span><div className="w-6 h-[1px] bg-slate-300"></div></div><div className="flex items-center gap-1"><span className="text-[6px]">2.</span><div className="w-6 h-[1px] bg-slate-300"></div></div></div></button>
                                <button onClick={() => { onAction('format', { isNumbered: true, isBulleted: false, listType: 'number-decimal-paren' }); setShowNumberingMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1"><div className="flex flex-col gap-1 items-start w-full px-1"><div className="flex items-center gap-1"><span className="text-[6px]">1)</span><div className="w-6 h-[1px] bg-slate-300"></div></div><div className="flex items-center gap-1"><span className="text-[6px]">2)</span><div className="w-6 h-[1px] bg-slate-300"></div></div></div></button>
                                <button onClick={() => { onAction('format', { isNumbered: true, isBulleted: false, listType: 'number-roman-lower' }); setShowNumberingMenu(false); }} className="col-span-1 aspect-square flex flex-col items-center justify-center hover:bg-slate-50 border border-slate-200 rounded p-1 gap-1"><div className="flex flex-col gap-1 items-start w-full px-1"><div className="flex items-center gap-1"><span className="text-[6px]">i.</span><div className="w-6 h-[1px] bg-slate-300"></div></div><div className="flex items-center gap-1"><span className="text-[6px]">ii.</span><div className="w-6 h-[1px] bg-slate-300"></div></div></div></button>
                            </div>
                        )}
                    </div>

                    <div className="w-[1px] h-4 bg-slate-100 mx-1.5" />

                    <div className="relative flex items-center">
                        <button
                            className="w-7 h-[22px] flex flex-col items-center justify-center rounded-[3px] hover:bg-slate-100 transition-all relative"
                            title="Font Color"
                        >
                            <span className="text-[13px] font-black text-slate-800 leading-none">A</span>
                            <div className="w-4 h-[2.5px] rounded-sm mt-0.5" style={{ backgroundColor: context?.color || '#000000' }} />
                            <input
                                type="color"
                                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                value={context?.color || '#000000'}
                                onChange={(e) => onAction('format', { color: e.target.value })}
                            />
                        </button>
                        <ChevronDown size={8} className="text-slate-400 -ml-0.5" />
                    </div>

                    <div className="relative flex items-center">
                        <button
                            className="w-7 h-[22px] flex flex-col items-center justify-center rounded-[3px] hover:bg-slate-100 transition-all relative"
                            title="Highlight Color"
                        >
                            <Highlighter size={14} strokeWidth={2.5} className={context?.highlightColor ? "text-slate-900" : "text-slate-500"} />
                            <div className="w-4 h-[2.5px] rounded-sm mt-0.5" style={{ backgroundColor: context?.highlightColor || 'transparent' }} />
                            <input
                                type="color"
                                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                value={context?.highlightColor || '#ffff00'}
                                onChange={(e) => onAction('format', { highlightColor: e.target.value })}
                            />
                        </button>
                        <ChevronDown size={8} className="text-slate-400 -ml-0.5" />
                    </div>

                    <button
                        onClick={() => onAction('format', { isBold: false, isItalic: false, isUnderline: false, highlightColor: '', color: '#000000' })}
                        className="p-1 rounded-[3px] text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-all"
                        title="Clear Formatting"
                    >
                        <Eraser size={14} strokeWidth={2.5} />
                    </button>

                    {/* Alignment 2x2 Grid */}
                    <div className="gap-0.5 ml-auto">
                        <button onClick={() => onAction('format', { textAlign: 'left' })} className={cn("p-1 rounded-[3px] transition-all", context?.textAlign === 'left' ? "bg-blue-100 text-blue-600" : "hover:bg-slate-100 text-slate-500")}><AlignLeft size={12} strokeWidth={2.5} /></button>
                        <button onClick={() => onAction('format', { textAlign: 'center' })} className={cn("p-1 rounded-[3px] transition-all", context?.textAlign === 'center' ? "bg-blue-100 text-blue-600" : "hover:bg-slate-100 text-slate-500")}><AlignCenter size={12} strokeWidth={2.5} /></button>
                        <button onClick={() => onAction('format', { textAlign: 'right' })} className={cn("p-1 rounded-[3px] transition-all", context?.textAlign === 'right' ? "bg-blue-100 text-blue-600" : "hover:bg-slate-100 text-slate-500")}><AlignRight size={12} strokeWidth={2.5} /></button>
                        <button onClick={() => onAction('format', { textAlign: 'justify' })} className={cn("p-1 rounded-[3px] transition-all", context?.textAlign === 'justify' ? "bg-blue-100 text-blue-600" : "hover:bg-slate-100 text-slate-500")}><AlignJustify size={12} strokeWidth={2.5} /></button>
                    </div>

                    {/* <div className="ml-auto flex items-center gap-1">
                        <button
                            onClick={onClose}
                            className="p-1.5 hover:bg-slate-100 text-slate-400 rounded-md transition-colors"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div> */}
                </div>
            </div>
        );
    }

    const safeXVertical = Math.max(10, Math.min(position.x, window.innerWidth - 300));
    const safeYVertical = Math.max(10, Math.min(position.y, window.innerHeight - 450));

    return (
        <div
            className="fixed z-[9999] w-72 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200"
            style={{
                left: `${safeXVertical}px`,
                top: `${safeYVertical}px`,
                maxHeight: '400px'
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="p-3 border-b border-slate-100 flex items-center gap-2 bg-slate-50/50">
                <Search size={14} className="text-slate-400" />
                <input
                    autoFocus
                    type="text"
                    placeholder="Type a command..."
                    className="bg-transparent border-none outline-none text-sm w-full font-medium text-slate-700 placeholder:text-slate-400"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            <div
                ref={scrollContainerRef}
                className="overflow-y-auto py-2 px-1 custom-scrollbar"
            >
                {filteredItems.length > 0 ? (
                    filteredItems.map((item, index) => {
                        const isSelected = index === selectedIndex;
                        const showsCategory = index === 0 || filteredItems[index - 1].category !== item.category;

                        return (
                            <React.Fragment key={item.id}>
                                {showsCategory && (
                                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-400">
                                        {item.category}
                                    </div>
                                )}
                                <button
                                    className={cn(
                                        "w-full flex items-center gap-3 px-3 py-2 text-left transition-all duration-150 rounded-lg group",
                                        isSelected ? "bg-slate-100" : "hover:bg-slate-50"
                                    )}
                                    onClick={() => item.action()}
                                    onMouseEnter={() => setSelectedIndex(index)}
                                >
                                    <div className={cn(
                                        "p-1.5 rounded-md transition-colors flex items-center justify-center relative",
                                        isSelected ? "bg-white shadow-sm text-blue-600" : "bg-slate-100 text-slate-500 group-hover:text-slate-700"
                                    )}>
                                        <item.icon size={16} />
                                    </div>
                                    <div className="flex flex-col min-w-0">
                                        <span className={cn(
                                            "text-sm font-semibold truncate",
                                            isSelected ? "text-slate-900" : "text-slate-700"
                                        )}>
                                            {item.label}
                                        </span>
                                        {item.description && (
                                            <span className="text-[11px] text-slate-400 truncate leading-tight">
                                                {item.description}
                                            </span>
                                        )}
                                    </div>
                                    {isSelected && (
                                        <div className="ml-auto text-[10px] font-mono font-bold text-slate-400 flex items-center gap-1 border border-slate-200 rounded px-1 bg-white">
                                            <span>↵</span>
                                        </div>
                                    )}
                                </button>
                            </React.Fragment>
                        );
                    })
                ) : (
                    <div className="px-3 py-8 text-center flex flex-col items-center gap-2">
                        <div className="p-3 bg-slate-50 rounded-full">
                            <Search size={20} className="text-slate-300" />
                        </div>
                        <span className="text-sm text-slate-400 font-medium whitespace-nowrap">No commands found for "{search}"</span>
                    </div>
                )}
            </div>

            <div className="px-3 py-2 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <div className="flex items-center gap-1 text-[10px] font-medium text-slate-400">
                    <span className="border border-slate-200 rounded px-1 bg-white">↑↓</span>
                    <span>to navigate</span>
                </div>
                <div className="flex items-center gap-1 text-[10px] font-medium text-slate-400">
                    <span className="border border-slate-200 rounded px-1 bg-white">Esc</span>
                    <span>to close</span>
                </div>
            </div>
        </div>
    );
};
